import * as React from "react"
import { Card, CardSection, CardHeader, Button, Text } from "gatsby-interface"
import {
  useCmsVendorsForGatsbyProvisionScriptQuery,
  useProvisionSetupDataForSiteMutation,
  useSiteProvisionJobChangedSubscription,
} from "../../DeployNow/queries.generated"
import {
  CmsVendor,
  CmsProvider,
  SiteProvisionStatus,
} from "@modules/graphql/types"
import { ProvisioningRawLogs } from "./ProvisioningRawLogs"
import { MdArrowForward, MdCheck } from "react-icons/md"
import { ErrorAlert } from "@modules/ui/components/ErrorAlert"
import { Integration } from "@modules/ui/components/Integration"
import useTracker from "@modules/analytics/hooks/useTracker"
import { SegmentEventType } from "@modules/analytics"
import { FormattedMessage } from "@modules/locales"
import { deployNow as text } from "@modules/locales/default.js"

const getCmsVendorLogo = (
  cmsVendor: CmsVendor,
  cmsIntegrations: CmsProvider[]
) => {
  const integration = cmsIntegrations.find(
    cmsIntegration => cmsIntegration.name === cmsVendor
  )

  return integration ? (
    <Integration.Vendor image={integration?.logoUrl} name={integration?.name} />
  ) : null
}

type CmsProvisioningCardProps = {
  siteId: string
  organizationId: string
  cmsIntegrations: CmsProvider[]
  setDisableCreateSite: (_disabled: boolean) => void
}

export function CmsProvisioningCard({
  siteId,
  organizationId,
  cmsIntegrations,
  setDisableCreateSite,
}: CmsProvisioningCardProps) {
  const { trackSegment } = useTracker()
  const {
    data: cmsVendorsToProvision,
  } = useCmsVendorsForGatsbyProvisionScriptQuery({
    variables: {
      siteId,
    },
  })

  const provisionCmsVendors =
    cmsVendorsToProvision?.cmsVendorsForGatsbyProvisionScript

  const [
    currentProvisionStatus,
    setCurrentProvisionStatus,
  ] = React.useState<SiteProvisionStatus | null>(null)

  const [
    provisionSetupDataForSiteMutation,
    {
      data: provisionJobData,
      loading: provisionJobLoading,
      error: provisionJobError,
    },
  ] = useProvisionSetupDataForSiteMutation()

  const provisionJobId =
    provisionJobData?.provisionSetupDataForSite.siteProvisionJob?.id

  useSiteProvisionJobChangedSubscription({
    variables: {
      id: provisionJobId ? provisionJobId : "",
    },
    skip: !provisionJobId,
    onSubscriptionData: ({ subscriptionData }) => {
      if (subscriptionData.data?.siteProvisionJobChanged?.status) {
        const status = subscriptionData.data.siteProvisionJobChanged.status
        setCurrentProvisionStatus(status)
        // track successful and failed provisioning
        if (
          status === SiteProvisionStatus.Success ||
          status === SiteProvisionStatus.Failed
        ) {
          const variant =
            status === SiteProvisionStatus.Success ? `Succeeded` : `Failed`
          trackSegment({
            type: SegmentEventType.Track,
            event: `Provision CMS Data ${variant}`,
            properties: {
              cms: provisionCmsVendors?.[0]
                ? provisionCmsVendors[0]
                : CmsVendor.None,
              siteId,
            },
          })
          setDisableCreateSite(false)
        }
      }
    },
  })

  // track detection
  React.useEffect(() => {
    if (!!provisionCmsVendors) {
      trackSegment({
        type: SegmentEventType.Track,
        event: `Gatsby Provision Script Detected`,
        properties: {
          cms: provisionCmsVendors?.[0],
          siteId,
        },
      })
    }
  }, [provisionCmsVendors])

  const provisionSiteData = async () => {
    // only allow call to kick off provisioning job if it is
    // the first time or it ended in failure
    if (
      currentProvisionStatus === null ||
      (!!currentProvisionStatus &&
        currentProvisionStatus === SiteProvisionStatus.Failed)
    ) {
      // track execution of provisioning script
      trackSegment({
        type: SegmentEventType.Track,
        event: `Provision CMS Data Clicked`,
        properties: {
          cms: provisionCmsVendors?.[0]
            ? provisionCmsVendors[0]
            : CmsVendor.None,
          siteId,
        },
      })
      const result = await provisionSetupDataForSiteMutation({
        variables: {
          siteId,
          workspaceId: organizationId,
        },
      })
      const newProvisionJob =
        result.data?.provisionSetupDataForSite?.siteProvisionJob
      // set initial status
      if (newProvisionJob?.status) {
        setCurrentProvisionStatus(newProvisionJob.status)
        setDisableCreateSite(true)
      }
    }
  }

  return provisionCmsVendors && provisionCmsVendors.length > 0 ? (
    <Card>
      <CardHeader applyPaddingBottom={false} title="Provision CMS Data" />
      <CardSection>
        <Text
          css={theme => ({
            color: theme.colors.grey[50],
          })}
        >
          <FormattedMessage<never, "span">
            message={text.headers.provisionScriptStep}
            tags={{
              span: function(content) {
                return (
                  <span css={{ fontFamily: `monospace`, whiteSpace: `pre` }}>
                    {content}
                  </span>
                )
              },
            }}
          />
        </Text>
        <div
          css={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div>
            {/* For now we only expect one vendor to be returned, hence passing index 0, but the API is designed to support multiple for future use case*/}
            {getCmsVendorLogo(provisionCmsVendors[0], cmsIntegrations)}
          </div>
          {currentProvisionStatus === SiteProvisionStatus.Success ? (
            <Button rightIcon={<MdCheck />} tone="SUCCESS">
              {text.actions.provisionData}
            </Button>
          ) : (
            <Button
              loading={
                provisionJobLoading ||
                (!!currentProvisionStatus &&
                  currentProvisionStatus !== SiteProvisionStatus.Failed)
              }
              loadingLabel="Provisioning data"
              rightIcon={<MdArrowForward />}
              onClick={provisionSiteData}
            >
              {text.actions.provisionData}
            </Button>
          )}
        </div>
      </CardSection>
      <CardSection>
        {provisionJobError && (
          <ErrorAlert>
            Error creating provision job: {provisionJobError.message}
          </ErrorAlert>
        )}
        {provisionJobId && currentProvisionStatus && (
          <ProvisioningRawLogs
            jobId={provisionJobId}
            siteId={siteId}
            currentProvisionStatus={currentProvisionStatus}
          />
        )}
      </CardSection>
    </Card>
  ) : null
}
